import { configBaseSelector, type ReferencedConfigInputType, useFetchConfigDetails } from '@repo/config-services';
import { LocalStorage } from '@vectord/utils';
import { type BaseOperatorType } from 'advanced-filters/data/options';
import { filterActions, type RootState } from 'advanced-filters/store';
import {
  setSelectedEntity as setSelectedEntityAction,
} from 'advanced-filters/store/filter';
import { type FilterLevel } from 'advanced-filters/types/filter';
import {
  getReferencedDataFromAppliedFilters,
  updateFilterConditionsBasedOnConfigDetails,
} from 'advanced-filters/utils/filterUtils';
import type React from 'react';
import {
  createContext, useContext, useEffect, useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareCallback } from 'use-deep-compare';

interface SelectedEntityContextProps {
  selectedEntity?: string;
}

const SelectedEntityContext = createContext<SelectedEntityContextProps | undefined>(undefined);

const lsInstance = LocalStorage.getInstance('store.advancedFilters');

export function SelectedEntityProvider({
  children,
  entity,
}: {
  children: React.ReactNode;
  entity: string;
}) {
  const dispatch = useDispatch();
  const selectedEntity = useSelector((state: RootState) => state.advancedFilters.filter.selectedEntity);
  const [appliedFilters, setAppliedFilters] = useState<{
    levels: FilterLevel[];
    comparator: BaseOperatorType;
  } | null>(null);
  const configData = useSelector(configBaseSelector);

  const [referencedList, setReferencedList] = useState<ReferencedConfigInputType>({
    leadAttributes: [],
    users: [],
    activities: [],
    salesGroups: [],
    tasks: [],
    leadAttributeOptions: [],
  });

  const callbackToUpdateFilterWithConfigInfo = useDeepCompareCallback(() => {
    if (appliedFilters?.levels) {
      const updatedFilters = updateFilterConditionsBasedOnConfigDetails(configData, appliedFilters?.levels);
      // Check if we're dealing with levels or filters
      if (Array.isArray(updatedFilters)
        && updatedFilters.length > 0
        && updatedFilters[0]
        && 'level' in updatedFilters[0]) {
        // Handle levels format
        dispatch(filterActions.setFilter({
          levels: updatedFilters as any,
          comparator: appliedFilters?.comparator,
        }));
      }
      // Reset applied filters once applied
      setAppliedFilters(null);
    }
  }, [appliedFilters?.comparator, appliedFilters?.levels, configData, dispatch]);

  useFetchConfigDetails(referencedList, callbackToUpdateFilterWithConfigInfo);

  useEffect(() => {
    // Set the selected entity
    dispatch(setSelectedEntityAction(entity));

    // Load stored filters for this entity
    const storedData = lsInstance?.config || {};
    const storedFilters = storedData[entity];

    // If we have stored filters, set them
    if (storedFilters) {
      dispatch(filterActions.setFilter({
        levels: storedFilters.levels,
        comparator: storedFilters.comparator,
      }));
      const { referencedData } = getReferencedDataFromAppliedFilters(storedFilters.levels);
      setReferencedList(referencedData);
      setAppliedFilters({
        levels: storedFilters.levels,
        comparator: storedFilters.comparator,
      });
    }

    return () => {
      dispatch(setSelectedEntityAction(undefined));
    };
  }, [entity, dispatch]);

  const value = useMemo(() => ({ selectedEntity }), [selectedEntity]);

  return (
    <SelectedEntityContext.Provider value={value}>
      {children}
    </SelectedEntityContext.Provider>
  );
}

export const useSelectedEntity = (): SelectedEntityContextProps => {
  const context = useContext(SelectedEntityContext);
  if (!context) {
    throw new Error('useSelectedEntity must be used within a SelectedEntityProvider');
  }
  return context;
};
